<template>
  <div class="table">
    <div class="table__row">
      <div class="table__icon"><img :src="walletIcon" /></div>
      <div class="table__content">
        <span class="label">{{ $t('common.walletName') }}</span>
        <span class="value"> {{ walletName }} </span>
      </div>
    </div>
    <div class="table__row">
      <div class="table__icon">
        <IdentificationIcon
          :address="address.toString()"
          size="39"
        ></IdentificationIcon>
      </div>
      <div class="table__content">
        <span class="label">{{ $t('common.accountAddress') }}</span>
        <span
          :title="address.toString()"
          class="value address flex flex-row"
          @click="copyAddress"
        >
          {{ shortAddress(address.toString(), 11, 10) }}
          <PButton
            v-tooltip="$t('common.clickToCopy')"
            class="copy"
            @click="copyAddress"
            ><img src="@/assets/copy.svg"
          /></PButton>
        </span>
      </div>
    </div>
  </div>
  <PButton
    class="p-button-primary w-full relative flex align-items-center justify-content-center mt-5"
    @click="disconnectWallet"
  >
    {{ $t('common.disconnect') }}
  </PButton>
  <CopyNotification ref="dialog" />
</template>
<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue-demi';
import { useClipboard } from '@vueuse/core';
import { shortAddress } from '@/utils';
import { IdentificationIcon } from '@/components/UI/IdentificationIcon';
import {
  AccountInfo,
  useWalletPlugin,
  WalletInfo,
} from '@/composables/useWalletPlugin';
import { CopyNotification } from '../CopyNotification';

const { copy: onCopyAddress } = useClipboard();
const { disconnect } = useWalletPlugin();
const dialog = ref();

type IProps = {
  account: AccountInfo | null;
  wallet: WalletInfo | null;
};

const props = defineProps<IProps>();
const emit = defineEmits(['disconnect']);

const { account, wallet } = toRefs(props);

const address = computed(() => account.value?.address || '');
const walletName = computed(() => wallet?.value?.name || '');
const walletIcon = computed(() => wallet?.value?.icon || '');

function copyAddress() {
  onCopyAddress(String(address.value));
  dialog.value.show();
}

function disconnectWallet() {
  disconnect();
  emit('disconnect');
}
</script>

<style lang="scss" scoped>
.copy {
  padding: 0;
  margin-left: 0.5rem;
  background: transparent;
  border: 0;
  border-radius: 0;
  &:hover {
    border: 0;
    background: transparent;
    color: #e5e4fa;
  }
}

.account-exit {
  font-weight: 500;
  font-size: 17px;
  padding: 15px 24px;
}

.table {
  border: 1px solid transparentize(#e5e4fa, 0.85);
  border-radius: 16px;

  &__row {
    padding: 12px 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    color: var(--text-color);
    font-size: 17px;

    &:not(:last-child) {
      border-bottom: 1px solid transparentize(#e5e4fa, 0.85);
    }
  }
  &__icon {
    width: 20%;
    align-items: center;
    display: flex;
    img {
      width: 36px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    .label {
      font-weight: 500;
      font-size: 13px;
      line-height: 15px;
      color: #e5e4fa;
      opacity: 0.5;
    }
    .value {
      font-weight: 400;
      font-size: 17px;
      line-height: 20px;
      color: #e5e4fa;
      margin-top: 0.5rem;
    }
    .address {
      cursor: pointer;
    }
  }
}
</style>
